import { useEffect, useMemo, useRef, useState } from "react";
import { Helmet } from "react-helmet";
import { useHreflang } from "../../i18n/hooks/useHreflang";
import { useDispatch, useSelector } from "react-redux";
import { useHistory, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import Button from "../../components/button/Button";
import IconHeader from "../../components/icon-header/IconHeader";
import Container from "../../components/layout/containers/Container";
import Product, { ProductHeader } from "../../components/products/Product";
import { addToShoppingcart, getCategoriesRequest, getProductsRequest, getShippingRequest, ResetShoppingcart } from "../../redux/actions";
import { getSubscriptionRequest, startTrialRequest } from "../../redux/actions/subscription";
import { AppState } from "../../redux/types";
import { classNames } from "../../utils/classnames";
import { buildTranslatedPath, getLocaleFromPath, Locale, localePath } from "../../i18n/config";
import { metadataTranslations } from "../../i18n/metadata";
import { CATEGORIES, categoryOptions, formatRegionDetails, formatRegionOption, REGIONS, USER_TYPE } from "../../utils/constants";
import { getCurrencySymbol, numericPrice, displayPrice } from "../../utils/currency";
import { getDisplayRegion } from "../../utils/regionBootstrap";
import styles from "./products.module.css";

const labels: Record<Locale, {
    activeSubscription: string;
    canceledSubscription: string;
    close: string;
    goToCart: string;
    inUse: string;
    notAvailable: string;
    readMore: string;
    regionConfirm: string;
    regionConfirmed: string;
    regionPrompt: string;
    selectedProducts: string;
    tabletHeader: string;
    alarmHeader: string;
    total: string;
    tryForFree: string;
    trialSubscription: string;
}> = {
    fi: {
        activeSubscription: "Sinulla on jo aktiivinen Digihappy appi tilaus.",
        canceledSubscription: "Sinulla on jo Digihappy appi tilaus, joka on peruutettu. Voit nauttia jäljellä olevista päivistä, koska tilaus on jo maksettu.",
        close: "Sulje",
        goToCart: "Siirry koriin",
        inUse: "Käyttäjätunnuksesi on käytössä, ota yhteyttä digihappy@digihappy.fi",
        notAvailable: "Ei saatavilla alueellasi",
        readMore: "Lue lisää",
        regionConfirm: "Jatka",
        regionConfirmed: "Valinta vahvistettu",
        regionPrompt: "Vahvista maa ja kieli ennen ostamista.",
        selectedProducts: "Valitut tuotteet",
        tabletHeader: "Valitse Senioritablettipalvelu",
        alarmHeader: "Valitse Turvahälytinpalvelu",
        total: "Yhteensä",
        tryForFree: "Kokeile maksutta",
        trialSubscription: "Sinulla on jo käynnissä oleva Digihappy appi kokeilujakso.",
    },
    en: {
        activeSubscription: "You already have an active Digihappy app subscription.",
        canceledSubscription: "You already have a canceled Digihappy app subscription. You can continue using the remaining paid days.",
        close: "Close",
        goToCart: "Go to cart",
        inUse: "Your username is in use, please contact digihappy@digihappy.fi",
        notAvailable: "Not available in your region",
        readMore: "Read more",
        regionConfirm: "Continue",
        regionConfirmed: "Selection confirmed",
        regionPrompt: "Confirm your country and language before buying.",
        selectedProducts: "Selected products",
        tabletHeader: "Choose a senior tablet service",
        alarmHeader: "Choose a safety alarm service",
        total: "Total",
        tryForFree: "Try for free",
        trialSubscription: "You already have an active Digihappy app trial.",
    },
    sv: {
        activeSubscription: "Du har redan en aktiv Digihappy-appprenumeration.",
        canceledSubscription: "Du har redan en avslutad Digihappy-appprenumeration. Du kan fortsätta använda de återstående betalda dagarna.",
        close: "Stäng",
        goToCart: "Gå till varukorgen",
        inUse: "Ditt användarnamn används redan, kontakta digihappy@digihappy.fi",
        notAvailable: "Inte tillgänglig i din region",
        readMore: "Läs mer",
        regionConfirm: "Fortsätt",
        regionConfirmed: "Valet bekräftat",
        regionPrompt: "Bekräfta land och språk innan du köper.",
        selectedProducts: "Valda produkter",
        tabletHeader: "Välj seniorplattetjänst",
        alarmHeader: "Välj trygghetslarmstjänst",
        total: "Totalt",
        tryForFree: "Prova gratis",
        trialSubscription: "Du har redan en aktiv provperiod för Digihappy-appen.",
    },
};

const getPeriodLabel = (locale: Locale, period?: string | number) => {
    const count = Number(period || 1);
    if (locale === "en") return `${count} ${count === 1 ? "month" : "months"}`;
    if (locale === "sv") return `${count} mån`;
    return `${count} kk`;
};

const ProductsScreen = () => {
    const { cat } = useParams<{ cat: string }>();
    const ostaHreflang = useHreflang("osta");
    const locale = getLocaleFromPath();
    const t = labels[locale];
    const category = CATEGORIES[cat as keyof typeof CATEGORIES] as "tablet" | "phone" | "alarm" | "app" | "accessories";
    const user_subscription = useSelector((state: AppState) => state.subscription.get?.subscriptionData);

    const dispatch = useDispatch();
    const user = useSelector((state: AppState) => state.user);
    const history = useHistory();
    const regionCode = useSelector((state: AppState) => state.region?.code);
    const pricesLoading = useSelector((state: AppState) => state.region?.loading);
    const customerTrialEmailId = useSelector((state: AppState) => state.categories.category?.attributes?.customerTrialEmailId);
    const displayRegion = getDisplayRegion(regionCode, window.location.pathname);
    const [confirmedRegion, setConfirmedRegion] = useState(() => sessionStorage.getItem("confirmedBuyRegion"));
    const [isRegionMenuOpen, setIsRegionMenuOpen] = useState(false);
    const regionMenuRef = useRef<HTMLDivElement>(null);
    const isRegionConfirmed = confirmedRegion === regionCode;
    const isInteractionLocked = !isRegionConfirmed;

    useEffect(() => {
        if (confirmedRegion && confirmedRegion !== regionCode) {
            setConfirmedRegion(null);
            sessionStorage.removeItem("confirmedBuyRegion");
        }
    }, [confirmedRegion, regionCode]);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (regionMenuRef.current && !regionMenuRef.current.contains(event.target as Node)) {
                setIsRegionMenuOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    useEffect(() => {
        dispatch(getCategoriesRequest(category));
        return () => {};
    }, [dispatch, category, regionCode]);

    useEffect(() => {
        dispatch(getShippingRequest());
    }, [dispatch]);

    const _categories = useSelector((state: AppState) => state.categories.category?.attributes.sub_categories?.data);
    const categories = useMemo(() => {
        return _categories?.sort((a, b) => a.attributes.SortNumber - b.attributes.SortNumber);
    }, [_categories]);

    const categoryIds = useMemo(() => categories?.map((c) => c.attributes.slug), [categories]);

    useEffect(() => {
        dispatch(getProductsRequest());
        return () => {};
    }, [dispatch]);

    useEffect(() => {
        if (categoryIds && categoryIds.length > 0) {
            dispatch(ResetShoppingcart());
        }
    }, [categoryIds, dispatch]);

    const _products = useSelector((state: AppState) => state.products.products);
    const products = useMemo(() => {
        return _products.filter((p) => categoryIds?.includes(p.category));
    }, [_products, categoryIds]);

    const visibleCategories = useMemo(() => {
        if (!categories) return [];
        return categories.filter((cat) =>
            products.some((p) => p.category === cat.attributes.slug)
        );
    }, [categories, products]);

    useEffect(() => {
        if (pricesLoading) return;
        products.forEach((p) => {
            if ((p.locked || p.selected) && p.isAvailable !== false) {
                dispatch(addToShoppingcart(p));
            }
        });
        return () => {};
    }, [dispatch, products, pricesLoading]);

    useEffect(() => {
        if (user?.id) {
            dispatch(getSubscriptionRequest(user.id));
        }
    }, [dispatch, user?.id]);

    const cartItems = useSelector((state: AppState) => state.cart.inCart);
    const allProducts = useSelector((state: AppState) => state.products.products);

    // Enrich cart items with latest region prices from products store
    const selectedProducts = cartItems.map((item) => {
        const fresh = allProducts.find((p) => p.id === item.id);
        return fresh ?? item;
    });

    const regionCurrency = getCurrencySymbol(regionCode ?? "FI");
    const totalPrice = selectedProducts.reduce((total, item) => total + numericPrice(item), 0);
    const metaTablet = metadataTranslations.products_tablet[locale] || metadataTranslations.products_tablet.fi;
    const metaPhone = metadataTranslations.products_phone[locale] || metadataTranslations.products_phone.fi;
    const metaAlarm = metadataTranslations.products_alarm[locale] || metadataTranslations.products_alarm.fi;
    const metaApp = metadataTranslations.products_app[locale] || metadataTranslations.products_app.fi;
    const metaAccessories = metadataTranslations.products_accessories[locale] || metadataTranslations.products_accessories.fi;

    const title = {
        tablet: metaTablet.title,
        phone: metaPhone.title,
        alarm: metaAlarm.title,
        app: metaApp.title,
        accessories: metaAccessories.title,
    };
    const description = {
        tablet: metaTablet.description,
        phone: metaPhone.description,
        alarm: metaAlarm.description,
        app: metaApp.description,
        accessories: metaAccessories.description,
    };

    const subscriptionType = () => {
        if (user_subscription && user_subscription.status === "active" && user.appOnly) {
            return "ACTIVE";
        }
        if (user_subscription && user_subscription.status === "trialing" && user.appOnly) {
            return "TRIAL";
        }

        if (user_subscription && user_subscription.status === "cancelling" && user.appOnly) {
            return "CANCELED";
        }

        return "NONE";
    };

    const startTrial = () => {
        dispatch(startTrialRequest(history, customerTrialEmailId, regionCode));
    };

    const handleTryForFree = () => {
        if (!isRegionConfirmed) return;

        if (!user.isLoggedIn) {
            history.push(localePath("luo_tili"));
            return;
        }

        const subType = subscriptionType();
        const toastOptions: any = {
            hideProgressBar: true,
            position: "top-center",
            theme: "colored",
        };

        switch (subType) {
            case "ACTIVE":
                toast.info(t.activeSubscription, toastOptions);
                break;

            case "CANCELED":
                toast.info(t.canceledSubscription, toastOptions);
                break;

            case "TRIAL":
                toast.info(t.trialSubscription, toastOptions);
                break;

            default:
                if (user.userType === USER_TYPE.normal) {
                    startTrial();
                } else {
                    history.push(localePath("tili", "tilaus"));
                }
        }
    };

    const isEligibleBuyer = () => {
        const isAppOnly = user.isLoggedIn && user.userType === USER_TYPE.appOnly;
        const isTabletOrPhone = [categoryOptions.tablet, categoryOptions.phone].includes(category);

        return !(isAppOnly && isTabletOrPhone);
    };

    const handleGoToCart = () => {
        if (!isRegionConfirmed) return;

        if (isEligibleBuyer()) {
            history.push(`${localePath("ostoskori")}?category=${category}`);
        } else {
            toast.info(t.inUse, {
                hideProgressBar: true,
                position: "top-center",
                theme: "colored",
            });
        }
    };

    const handleRegionSelect = (nextRegionCode: string) => {
        const selected = REGIONS.find((region) => region.code === nextRegionCode);
        if (!selected || selected.code === regionCode) return;
        const currentLang = localStorage.getItem("language") || "fi";
        const langChanging = selected.lang !== currentLang;
        const newPath = langChanging ? buildTranslatedPath(window.location.pathname, selected.lang, currentLang) + window.location.search : undefined;

        window.dispatchEvent(
            new CustomEvent("regionChangeRequested", {
                detail: { regionCode: selected.code, newLang: selected.lang, prevLang: currentLang, newPath },
            }),
        );
    };

    return (
        <>
            <Helmet>
                <title>{title[category]}</title>
                <meta name="description" content={description[category]} />
                <link rel="alternate" hrefLang="fi" href={`${ostaHreflang.fi}/${cat}`} />
                <link rel="alternate" hrefLang="en" href={`${ostaHreflang.en}/${cat}`} />
                <link rel="alternate" hrefLang="sv" href={`${ostaHreflang.sv}/${cat}`} />
                <link rel="alternate" hrefLang="x-default" href={`${ostaHreflang.fi}/${cat}`} />
            </Helmet>
            <Container>
                <div data-wg-notranslate="true">
                    {category === "tablet" && <IconHeader icon="/assets/icons/group.svg" text={t.tabletHeader} className={styles.header} />}
                    {category === "alarm" && <IconHeader icon="/assets/icons/group.svg" text={t.alarmHeader} className={styles.header} />}
                </div>
                <div className={classNames(styles.regionConfirm, { [styles.regionConfirmDone]: isRegionConfirmed })} data-wg-notranslate="true">
                    <div>
                        <div className={styles.regionConfirmTitle}>{isRegionConfirmed ? t.regionConfirmed : t.regionPrompt}</div>
                        <div className={styles.regionConfirmValue}>
                            <span>{formatRegionOption(displayRegion)}</span>
                            <span>{formatRegionDetails(displayRegion)}</span>
                        </div>
                    </div>
                    {!isRegionConfirmed && (
                        <div className={styles.regionConfirmActions}>
                            <div className={styles.regionConfirmSelectWrapper} ref={regionMenuRef}>
                                <button
                                    type="button"
                                    className={styles.regionConfirmSelectTrigger}
                                    onClick={() => setIsRegionMenuOpen((open) => !open)}
                                >
                                    {formatRegionOption(displayRegion)} <span className={styles.regionConfirmChevron}>▾</span>
                                </button>
                                {isRegionMenuOpen && (
                                    <div className={styles.regionConfirmMenu}>
                                        {REGIONS.map((region) => (
                                            <button
                                                key={region.code}
                                                type="button"
                                                className={classNames(styles.regionConfirmMenuItem, {
                                                    [styles.regionConfirmMenuItemActive]: region.code === (regionCode ?? "FI"),
                                                })}
                                                onPointerDown={(event) => {
                                                    event.preventDefault();
                                                    event.stopPropagation();
                                                    setIsRegionMenuOpen(false);
                                                    handleRegionSelect(region.code);
                                                }}
                                                onTouchStart={(event) => {
                                                    event.preventDefault();
                                                    event.stopPropagation();
                                                    setIsRegionMenuOpen(false);
                                                    handleRegionSelect(region.code);
                                                }}
                                                onClick={() => {
                                                    setIsRegionMenuOpen(false);
                                                    handleRegionSelect(region.code);
                                                }}
                                            >
                                                <span className={styles.regionConfirmMenuMain}>{formatRegionOption(region)}</span>
                                                <span className={styles.regionConfirmMenuSub}>{formatRegionDetails(region)}</span>
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>
                            <Button
                                className={styles.regionConfirmButton}
                                onClick={() => {
                                    sessionStorage.setItem("confirmedBuyRegion", regionCode ?? "FI");
                                    setConfirmedRegion(regionCode ?? "FI");
                                }}
                            >
                                {t.regionConfirm}
                            </Button>
                        </div>
                    )}
                </div>
                <div className={styles.container} data-wg-notranslate="true">
                    {pricesLoading ? (
                        <div className={styles.skeletonContainer}>
                            {[0, 1, 2].map((i) => (
                                <div className={styles.skeletonCard} key={i}>
                                    <div className={styles.skeletonBox} />
                                    <div className={styles.skeletonBox}>
                                        <div className={styles.skeletonLine} style={{ width: "60%" }} />
                                        <div className={styles.skeletonLine} style={{ width: "90%" }} />
                                        <div className={styles.skeletonLine} style={{ width: "75%" }} />
                                        <div className={styles.skeletonLine} style={{ width: "50%" }} />
                                    </div>
                                    <div className={styles.skeletonBox}>
                                        <div className={styles.skeletonLine} style={{ width: "40%" }} />
                                        <div className={styles.skeletonLine} style={{ width: "55%" }} />
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div className={classNames(styles.productsGate, { [styles.productsGateLocked]: isInteractionLocked })}>
                            {visibleCategories.map((category, key) => (
                                <div className={styles.categoryContainer} key={key}>
                                    <ProductHeader category={category} />
                                    <div className={styles.productsContainer}>
                                        {products
                                            .filter((product) => product.category === category.attributes.slug)
                                            .sort((a, b) => (a.sortNumber || 0) - (b.sortNumber || 0))
                                            .map((product, key) => {
                                                return (
                                                    <Product
                                                        product={product}
                                                        optional={category.attributes.Optional}
                                                        single={category.attributes.Selection === "Single"}
                                                        labels={t}
                                                        interactionLocked={isInteractionLocked}
                                                        key={key}
                                                    />
                                                );
                                            })}
                                    </div>
                                </div>
                            ))}
                            {isInteractionLocked && <div className={styles.productsGateOverlay} aria-hidden="true" />}
                        </div>
                    )}
                    {category === categoryOptions.app ? (
                        <div className={styles.bottomLineContainer}>
                            <Button className={styles.cartButton} style={{ float: "right" }} onClick={handleTryForFree} disabled={!isRegionConfirmed || pricesLoading || !selectedProducts.some((p) => p.productId && p.isAvailable !== false)}>
                                {t.tryForFree}
                            </Button>
                        </div>
                    ) : (
                        <div className={styles.bottomLineContainer}>
                            <h5>{t.selectedProducts}</h5>
                            <div className={styles.cartContainer}>
                                <div className={styles.itemsContainer}>
                                    {selectedProducts
                                        .sort((a, b) => (a.sortNumber || 0) - (b.sortNumber || 0))
                                        .map((c, key) => (
                                            <div className={styles.cartItem} key={key}>
                                                <span className={styles.itemName}>{c.name}</span>
                                                <span className={styles.itemName}>
                                                    {pricesLoading ? <span className={styles.skeletonInline} /> : `${displayPrice(c, regionCurrency)} ${c.priceType === "recurring" ? ` / ${getPeriodLabel(locale, c.period)}` : ""}`}
                                                </span>
                                            </div>
                                        ))}
                                    <div className={styles.cartItem} style={{ marginTop: "3rem" }}>
                                        <span className={classNames(styles.itemName, styles.total)}>{t.total} </span>
                                        <span className={classNames(styles.itemName, styles.total)}>{pricesLoading ? <span className={styles.skeletonInline} /> : `${totalPrice}${regionCurrency}`}</span>
                                    </div>
                                </div>

                                <div className={styles.shoppingCart}>
                                    <div className={styles.cartImageContainer}>
                                        <img src="/assets/icons/cart.png" className={styles.cartImage} alt="C" />
                                        <span className={styles.cartNumber}>{selectedProducts.length}</span>
                                    </div>
                                    <Button className={styles.cartButton} onClick={handleGoToCart} disabled={!isRegionConfirmed || pricesLoading || !selectedProducts.some((p) => p.productId && p.isAvailable !== false)}>
                                        {t.goToCart}
                                    </Button>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </Container>
        </>
    );
};

export default ProductsScreen;
